import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { Box, Text, VStack } from '@chakra-ui/react';
import { ReducerStatus } from 'utils/constants';

/**
 * Reusable confirmation description component for quiz-related operations
 * Handles submit, save, clear, and import confirmation dialogs with consistent styling
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Type of confirmation: 'submit', 'save', 'clear', 'import', 'general', 'quiz', 'questions'
 * @param {string} props.mode - Operation mode: 'create' or 'update' (used for submit/clear types)
 * @param {string} props.tabName - Optional tab name for tab-specific descriptions: 'General', 'Settings', or 'Questions'
 * @param {Object} props.data - Data object containing relevant information for the confirmation type
 * @param {Array} props.data.questions - Array of quiz questions
 * @param {Object} props.data.subQuestFormInputs - Sub-quest form input values
 * @param {Object} props.data.pendingFormValues - Form values pending submission (for submit type)
 * @param {Object} props.data.formInputs - Quiz settings form input values
 * @param {string} props.data.importStatus - Import operation status (for import type)
 * @param {Object} props.data.importData - Imported data with summary (for import type)
 * @param {Array} props.data.importErrors - Array of import error messages (for import type)
 * @returns {JSX.Element} Confirmation description content
 */
export function QuizConfirmationDescription({
  type,
  mode = 'create',
  data = {},
  tabName = null,
}) {
  const {
    questions = [],
    subQuestFormInputs = {},
    pendingFormValues,
    importStatus,
    importData,
    importErrors = [],
  } = data;

  // Submit quiz confirmation
  const renderSubmitConfirmation = () => {
    if (!pendingFormValues) return '';

    const totalQuestions = questions.length;
    const totalMarks = questions.reduce(
      (sum, question) => sum + question.mark,
      0
    );
    const scoreInfo =
      pendingFormValues.scoreType === 'Percentage'
        ? `${pendingFormValues.passingScore}% passing requirement rate`
        : `${pendingFormValues.passingScore}/${totalMarks} points to pass`;

    const timeLimitInfo = pendingFormValues.hasTimeLimit
      ? `${pendingFormValues.timeLimit} minutes`
      : 'No time limit';

    const attemptInfo = pendingFormValues.unlimitedAttempts
      ? 'Unlimited attempts'
      : `${pendingFormValues.numberOfAttempts} attempts allowed`;

    const actionText = mode === 'create' ? 'create' : 'update';

    return (
      <>
        <Text mb={3}>
          Are you sure you want to {actionText} this quiz sub-quest?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Sub-quest Name:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.name || 'Untitled Quiz'}
            </Text>
          </Text>
          <Text>
            Total Questions:{' '}
            <Text as="span" fontWeight="bold">
              {totalQuestions}
            </Text>
          </Text>
          <Text>
            Total Marks:{' '}
            <Text as="span" fontWeight="bold">
              {totalMarks}
            </Text>
          </Text>
          <Text>
            Score Requirements:{' '}
            <Text as="span" fontWeight="bold">
              {scoreInfo}
            </Text>
          </Text>
          <Text>
            Time Limit:{' '}
            <Text as="span" fontWeight="bold">
              {timeLimitInfo}
            </Text>
          </Text>
          <Text>
            Attempts:{' '}
            <Text as="span" fontWeight="bold">
              {attemptInfo}
            </Text>
          </Text>
        </VStack>
      </>
    );
  };

  const renderSaveConfirmation = () => {
    const totalQuestions = questions.length;

    // Tab-specific save messages
    if (tabName) {
      let tabSpecificMessage = '';

      switch (tabName) {
        case 'General':
          tabSpecificMessage =
            'This will save your General information (name, code, objective, tags, owner groups and publication status) locally on your browser.';
          break;
        case 'Settings':
          tabSpecificMessage =
            'This will save your Quiz Settings (time limit, passing score, attempts, and other configurations) locally on your browser.';
          break;
        case 'Questions':
          tabSpecificMessage = `This will save ${totalQuestions} question(s) locally on your browser.`;
          break;
        default:
          tabSpecificMessage = '';
      }

      return (
        <>
          <Text fontWeight="bold" mb={3} color="green.600">
            Are you sure you want to save your current progress for the{' '}
            {tabName} tab?
          </Text>
          <VStack align="start" spacing={1}>
            <Text>{tabSpecificMessage}</Text>
            <Text fontSize="sm" color="gray.600" mt={2}>
              You can continue working on this quiz later, even after closing
              your browser.
            </Text>
          </VStack>
        </>
      );
    }

    // Default behavior (for CreateQuizPage or when tabName is not provided)
    return (
      <>
        <Text mb={3}>Are you sure you want to save your current progress?</Text>
        <VStack align="start" spacing={1}>
          <Text>
            This will save your current quiz settings, sub-quest information,
            and{' '}
            <Text as="span" fontWeight="bold">
              {totalQuestions} question(s) locally on your browser.
            </Text>
          </Text>
          <Text fontSize="sm" color="gray.600" mt={2}>
            You can continue working on this quiz later, even after closing your
            browser.
          </Text>
        </VStack>
      </>
    );
  };

  // Clear draft confirmation
  const renderClearConfirmation = () => {
    const totalQuestions = questions.length;
    const hasUnsavedProgress = subQuestFormInputs.name || totalQuestions > 0;

    if (mode === 'update') {
      // Tab-specific clear messages for update mode
      if (tabName) {
        let itemsList = '';
        let tabContext = '';

        switch (tabName) {
          case 'General':
            itemsList =
              '• Sub-quest form data (name, code, objective, tags, owner groups, publication status)';
            tabContext = 'General information';
            break;
          case 'Settings':
            itemsList =
              '• Quiz settings and configurations (time limit, passing score, attempts, etc.)';
            tabContext = 'Quiz Settings';
            break;
          case 'Questions':
            itemsList = `• All ${totalQuestions} unsaved question(s) and their content`;
            tabContext = 'Questions';
            break;
          default:
            itemsList = '';
            tabContext = '';
        }

        return (
          <>
            <Text mb={3} fontWeight="bold" color="red.600">
              Are you sure you want to clear the {tabContext} draft for this
              tab?
            </Text>
            <VStack align="start" spacing={2}>
              <Text>
                This will permanently remove unsaved changes from your local
                browser storage:
              </Text>
              <Box pl={4}>
                <Text>{itemsList}</Text>
              </Box>
              <Text fontSize="sm" color="gray.600" mt={2}>
                The form will revert to the latest saved{' '}
                {tabContext.toLowerCase()}.
              </Text>
            </VStack>
          </>
        );
      }

      // Default update mode behavior (when tabName is not provided)
      return (
        <>
          <Text mb={3} fontWeight="bold" color="red.600">
            Are you sure you want to clear all settings and questions?
          </Text>
          <VStack align="start" spacing={2}>
            <Text>This will permanently remove unsaved changes:</Text>
            <Box pl={4}>
              <Text>• Sub-quest form data</Text>
              <Text>• Quiz settings and configurations</Text>
              <Text>
                • All unsaved questions and their content from your local
                browser storage
              </Text>
            </Box>
            <Text fontSize="sm" color="gray.600" mt={2}>
              The form will revert to the latest saved settings.
            </Text>
          </VStack>
        </>
      );
    }

    return (
      <>
        <Text mb={3} fontWeight="bold" color="red.600">
          Are you sure you want to clear all settings and questions?
        </Text>
        <VStack align="start" spacing={2}>
          {hasUnsavedProgress && (
            <Text color="red.500" fontWeight="medium">
              ⚠️ This action cannot be undone!
            </Text>
          )}
          <Text>This will permanently remove:</Text>
          <Box pl={4}>
            <Text>• All quiz settings and configurations</Text>
            <Text>• Sub-quest form data</Text>
            <Text>
              • All {totalQuestions} question(s) and their content from your
              local browser storage
            </Text>
          </Box>
          <Text fontSize="sm" color="gray.600" mt={2}>
            You will need to start creating your quiz from scratch.
          </Text>
        </VStack>
      </>
    );
  };

  // Import quiz questions confirmation
  const renderImportConfirmation = () => {
    if (importStatus === ReducerStatus.FAILED) {
      return (
        <>
          <Text mb={3} fontWeight="bold" color="red.600">
            Failed to import questions
          </Text>
          <VStack align="start" spacing={2}>
            <Text>The following errors occurred:</Text>
            <Box pl={4} maxH="200px" overflowY="auto">
              {importErrors.map((error) => (
                <Text key={uuidv4()} color="red.500" fontSize="sm">
                  • {error}
                </Text>
              ))}
            </Box>
          </VStack>
        </>
      );
    }

    if (!importData) return '';

    const { summary } = importData;

    return (
      <>
        <Text mb={3}>
          {summary.hasErrors
            ? 'Import completed with some issues'
            : 'Ready to import questions'}
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Valid Questions:{' '}
            <Text as="span" fontWeight="bold" color="green.500">
              {summary.validQuestions}
            </Text>
          </Text>
          {summary.invalidQuestions > 0 && (
            <Text>
              Invalid Questions:{' '}
              <Text as="span" fontWeight="bold" color="red.500">
                {summary.invalidQuestions}
              </Text>
            </Text>
          )}
          <Text>
            Current Questions:{' '}
            <Text as="span" fontWeight="bold">
              {questions.length}
            </Text>
          </Text>
          <Text>
            Total After Import:{' '}
            <Text as="span" fontWeight="bold">
              {questions.length + summary.validQuestions}
            </Text>
          </Text>
        </VStack>

        {importErrors.length > 0 && (
          <Box mt={4}>
            <Text fontWeight="medium" color="orange.600" mb={2}>
              Issues found:
            </Text>
            <Box pl={4} maxH="150px" overflowY="auto">
              {importErrors.slice(0, 10).map((error) => (
                <Text key={uuidv4()} color="orange.500" fontSize="sm">
                  • {error}
                </Text>
              ))}
              {importErrors.length > 10 && (
                <Text fontSize="sm" color="gray.500">
                  ... and {importErrors.length - 10} more issues
                </Text>
              )}
            </Box>
          </Box>
        )}

        {summary.validQuestions > 0 && (
          <Text fontSize="sm" color="gray.600" mt={3}>
            Valid questions will be added to the end of your current question
            list.
          </Text>
        )}
      </>
    );
  };

  // General tab update confirmation
  const renderGeneralConfirmation = () => {
    return (
      <>
        <Text mb={3}>
          Are you sure you want to update the General information for this quiz
          sub-quest?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Sub-quest Name:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.name || 'Untitled'}
            </Text>
          </Text>
          <Text>
            Code:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.code || 'N/A'}
            </Text>
          </Text>
          <Text>
            Objective:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.objective || 'N/A'}
            </Text>
          </Text>
          {subQuestFormInputs.tags && subQuestFormInputs.tags.length > 0 && (
            <Text>
              Tags:{' '}
              <Text as="span" fontWeight="bold">
                {subQuestFormInputs.tags.join(' ') || 'N/A'}
              </Text>
            </Text>
          )}
          {subQuestFormInputs.groups &&
            subQuestFormInputs.groups.length > 0 && (
              <Text>
                Owner Groups:{' '}
                <Text as="span" fontWeight="bold">
                  {subQuestFormInputs.groups.length || 'N/A'} users/DL&apos;s
                  included
                </Text>
              </Text>
            )}
          <Text>
            Published:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.isPublished === 'true' ? 'Yes' : 'No'}
            </Text>
          </Text>
        </VStack>
      </>
    );
  };

  // Quiz settings update confirmation
  const renderQuizConfirmation = () => {
    if (!pendingFormValues) return '';

    const totalMarks = questions.reduce(
      (sum, question) => sum + question.mark,
      0
    );
    const scoreInfo =
      pendingFormValues.scoreType === 'Percentage'
        ? `${pendingFormValues.passingScore}% passing requirement rate`
        : `${pendingFormValues.passingScore}/${totalMarks} points to pass`;

    const timeLimitInfo = pendingFormValues.hasTimeLimit
      ? `${pendingFormValues.timeLimit} minutes`
      : 'No time limit';

    const attemptInfo = pendingFormValues.unlimitedAttempts
      ? 'Unlimited attempts'
      : `${pendingFormValues.numberOfAttempts} attempts allowed`;

    return (
      <>
        <Text mb={3}>Are you sure you want to update the Quiz Settings?</Text>
        <VStack align="start" spacing={1}>
          <Text>
            Score Type:{' '}
            <Text as="span" fontWeight="bold">
              {pendingFormValues.scoreType}
            </Text>
          </Text>
          <Text>
            Score Requirements:{' '}
            <Text as="span" fontWeight="bold">
              {scoreInfo}
            </Text>
          </Text>
          <Text>
            Time Limit:{' '}
            <Text as="span" fontWeight="bold">
              {timeLimitInfo}
            </Text>
          </Text>
          <Text>
            Attempts:{' '}
            <Text as="span" fontWeight="bold">
              {attemptInfo}
            </Text>
          </Text>
          <Text>
            Shuffle Questions:{' '}
            <Text as="span" fontWeight="bold">
              {pendingFormValues.isShuffled ? 'Yes' : 'No'}
            </Text>
          </Text>
          <Text>
            Show Answers:{' '}
            <Text as="span" fontWeight="bold">
              {pendingFormValues.showAnswers ? 'Yes' : 'No'}
            </Text>
          </Text>
        </VStack>
      </>
    );
  };

  // Questions update confirmation
  const renderQuestionsConfirmation = () => {
    const totalQuestions = questions.length;
    const totalMarks = questions.reduce(
      (sum, question) => sum + question.mark,
      0
    );

    return (
      <>
        <Text mb={3}>
          Are you sure you want to update the Questions for this quiz?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Total Questions:{' '}
            <Text as="span" fontWeight="bold">
              {totalQuestions}
            </Text>
          </Text>
          <Text>
            Total Marks:{' '}
            <Text as="span" fontWeight="bold">
              {totalMarks}
            </Text>
          </Text>
        </VStack>
        <Text fontSize="sm" color="gray.600" mt={2}>
          All questions will be saved with their current configuration.
        </Text>
      </>
    );
  };

  // Main render logic based on confirmation type
  switch (type) {
    case 'submit':
      return renderSubmitConfirmation();
    case 'save':
      return renderSaveConfirmation();
    case 'clear':
      return renderClearConfirmation();
    case 'import':
      return renderImportConfirmation();
    case 'general':
      return renderGeneralConfirmation();
    case 'quiz':
      return renderQuizConfirmation();
    case 'questions':
      return renderQuestionsConfirmation();
    default:
      return <Text>No confirmation type found: {type}</Text>;
  }
}

QuizConfirmationDescription.defaultProps = {
  mode: 'create',
  data: {},
  tabName: null,
};

QuizConfirmationDescription.propTypes = {
  type: PropTypes.oneOf([
    'submit',
    'save',
    'clear',
    'import',
    'general',
    'quiz',
    'questions',
  ]).isRequired,
  mode: PropTypes.oneOf(['create', 'update']),
  tabName: PropTypes.string,
  data: PropTypes.shape({
    questions: PropTypes.arrayOf(PropTypes.object),
    subQuestFormInputs: PropTypes.object,
    pendingFormValues: PropTypes.object,
    formInputs: PropTypes.object,
    importStatus: PropTypes.string,
    importData: PropTypes.shape({
      summary: PropTypes.shape({
        validQuestions: PropTypes.number,
        invalidQuestions: PropTypes.number,
        hasErrors: PropTypes.bool,
      }),
    }),
    importErrors: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default QuizConfirmationDescription;
